import { Button, ICON_COLOR, IconButton } from '@zep-us/zus';
import { BoltFillIcon, ChartLineIcon, PenPaperIcon, SquareStackIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';

/**
 * MobileOwnerLockedCTAButtons Props
 */
export interface MobileOwnerLockedCTAButtonsProps {
  /** 잠금된 퀴즈 복사 핸들러 */
  onDuplicateLocked: () => void;
  /** 플랜 업그레이드 핸들러 */
  onUpgradePlan: () => void;
  /** 학습 리포트 핸들러 */
  onGoToLms: () => void;
  /** 수정하기 핸들러 */
  onEdit: () => void;
}

/**
 * MobileOwnerLockedCTAButtons - 모바일용 소유자 잠금된 퀴즈 CTA 버튼
 *
 * @description
 * 모바일 하단 고정 영역에 최적화된 잠금된 퀴즈 소유자용 CTA 버튼입니다.
 * 단일 가로 레이아웃으로 구성됩니다.
 * - 버튼 줄: 플랜 결제 (Button, flex-1), 학습 리포트 (IconButton), 복사 (IconButton), 수정 (IconButton)
 *
 * Figma Reference: 28110-59313
 *
 * @example
 * ```tsx
 * <MobileOwnerLockedCTAButtons
 *   onDuplicateLocked={handleDuplicateLocked}
 *   onUpgradePlan={handleUpgradePlan}
 *   onGoToLms={handleGoToLms}
 *   onEdit={handleEdit}
 * />
 * ```
 */
export const MobileOwnerLockedCTAButtons = ({
  onDuplicateLocked,
  onUpgradePlan,
  onGoToLms,
  onEdit
}: MobileOwnerLockedCTAButtonsProps) => {
  const {
    t
  } = useTranslation(['common']);
  return <div className="flex w-full flex-col gap-xs" data-sentry-component="MobileOwnerLockedCTAButtons" data-sentry-source-file="MobileOwnerLockedCTAButtons.tsx">
      {/* 버튼 그룹 - 단일 가로 레이아웃 */}
      <div className="flex w-full flex-row gap-xs">
        {/* 플랜 결제 버튼 (flex-1로 남은 공간 차지) */}
        <Button size="md" variant="contained" intent="gradient" onClick={onUpgradePlan} leftSlot={<BoltFillIcon size="sm" fill={ICON_COLOR.inverse} />} className="flex-1" data-sentry-element="Button" data-sentry-source-file="MobileOwnerLockedCTAButtons.tsx">
          {t('detail.payPlan')}
        </Button>

        {/* 학습 리포트 버튼 (고정 너비 IconButton) */}
        <IconButton size="md" variant="contained" intent="alternative" onClick={onGoToLms} data-sentry-element="IconButton" data-sentry-source-file="MobileOwnerLockedCTAButtons.tsx">
          <ChartLineIcon size="sm" fill={ICON_COLOR.strong} data-sentry-element="ChartLineIcon" data-sentry-source-file="MobileOwnerLockedCTAButtons.tsx" />
        </IconButton>

        {/* 복사 버튼 (고정 너비 IconButton) */}
        <IconButton size="md" intent="neutral" variant="outlined" onClick={onDuplicateLocked} data-sentry-element="IconButton" data-sentry-source-file="MobileOwnerLockedCTAButtons.tsx">
          <SquareStackIcon size="sm" fill={ICON_COLOR.neutral} data-sentry-element="SquareStackIcon" data-sentry-source-file="MobileOwnerLockedCTAButtons.tsx" />
        </IconButton>

        {/* 수정 버튼 (고정 너비 IconButton) */}
        <IconButton size="md" intent="neutral" variant="outlined" onClick={onEdit} aria-label={t('detail.edit')} data-sentry-element="IconButton" data-sentry-source-file="MobileOwnerLockedCTAButtons.tsx">
          <PenPaperIcon size="sm" fill={ICON_COLOR.neutral} data-sentry-element="PenPaperIcon" data-sentry-source-file="MobileOwnerLockedCTAButtons.tsx" />
        </IconButton>
      </div>
    </div>;
};